import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { useNotify } from "../../shared/ui/feedback/SnackbarProvider";
import { patchPreferences } from "./api";
import { clearStored, readStored, writeStored } from "./storage";
import { DEFAULT_PREFERENCES, Preferences, sanitizePreferences } from "./types";

export const SAVE_DEBOUNCE_MS = 500;
export const LOCAL_ONLY_MESSAGE = "Sua preferência vale só neste navegador por enquanto";
export const REJECTED_MESSAGE = "Não foi possível salvar sua preferência.";

export type SetPref = <K extends keyof Preferences>(key: K, value: Preferences[K]) => void;
export type PreferencesContextValue = { prefs: Preferences; setPref: SetPref };

// Fora do provider vale o padrão A/A/A (e setPref não faz nada).
export const PreferencesContext = createContext<PreferencesContextValue>({ prefs: DEFAULT_PREFERENCES, setPref: () => {} });

const statusOf = (error: unknown) => (error as { response?: { status?: number } })?.response?.status;
const pendingValues = (userId: number): Partial<Preferences> => {
  const stored = readStored(userId);
  return stored?.pending ? stored.values : {};
};
// Pendência gravada antes do envio (write-ahead): recarga/logout no meio do debounce não perdem a escolha.
const savePending = (userId: number, values: Partial<Preferences>) =>
  writeStored(userId, { pending: true, values: { ...pendingValues(userId), ...values } });
// Tira da pendência só o que foi enviado com o mesmo valor: escolha mais nova feita durante o envio fica.
const dropSent = (userId: number, sent: Partial<Preferences>) => {
  const rest = Object.fromEntries(
    Object.entries(pendingValues(userId)).filter(
      ([key, value]) => JSON.stringify(sent[key as keyof Preferences]) !== JSON.stringify(value)
    )
  ) as Partial<Preferences>;
  if (Object.keys(rest).length) writeStored(userId, { pending: true, values: rest });
  else clearStored(userId);
};

export function PreferencesProvider({ children }: { children?: ReactNode }) {
  const { me } = useAuth();
  const notify = useNotify();
  const userId = me?.id ?? null;
  const [prefs, setPrefs] = useState<Preferences>(() => sanitizePreferences(me?.preferences));
  const queued = useRef<Partial<Preferences>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warned = useRef(false);
  const loadedFor = useRef<number | null>(null);
  const chain = useRef<Promise<void>>(Promise.resolve());
  const userRef = useRef(userId);
  userRef.current = userId;

  // Sempre manda junto o que ficou pendente: um sucesso nunca apaga uma escolha anterior não gravada.
  const send = useCallback(
    async (id: number, values: Partial<Preferences>) => {
      const payload = { ...pendingValues(id), ...values };
      if (Object.keys(payload).length === 0) return;
      try {
        await patchPreferences(payload);
        dropSent(id, payload);
      } catch (error) {
        const status = statusOf(error);
        if (status === 422) {
          // Rejeitado pelo contrato: descarta para não virar pendência eterna.
          dropSent(id, payload);
          notify({ severity: "error", message: REJECTED_MESSAGE });
          return;
        }
        // O que foi gravado depois (escolha mais nova) vence o payload que falhou.
        writeStored(id, { pending: true, values: { ...payload, ...pendingValues(id) } });
        if (status === 401 || warned.current) return; // 401: o interceptor já leva ao /login
        warned.current = true;
        notify({ severity: "info", message: LOCAL_ONLY_MESSAGE });
      }
    },
    [notify]
  );

  // Um PATCH por vez: cada envio lê a pendência só depois que o anterior gravou/limpou, então
  // uma falha atrasada não sobrescreve a escolha nova e um sucesso não apaga pendência não enviada.
  const enqueue = useCallback(
    (id: number, values: Partial<Preferences>) => {
      chain.current = chain.current.then(() => send(id, values)).catch(() => undefined); // a fila nunca trava
    },
    [send]
  );

  // Carga por usuário: me.preferences + pendência deste navegador. Aplicada DURANTE o render (padrão
  // "estado derivado" do React): se fosse num efeito, o shell pintaria um quadro no padrão A/A/A e
  // animaria o menu recolhido → aberto a cada carga (BUG UX-E2E-04).
  const [prefsUser, setPrefsUser] = useState<number | null>(null);
  if (userId != null && prefsUser !== userId) {
    setPrefsUser(userId);
    setPrefs({ ...sanitizePreferences(me?.preferences), ...pendingValues(userId) });
  }

  // Efeitos da carga (fora do render): reenvia por PATCH a pendência deste navegador.
  useEffect(() => {
    if (userId == null || loadedFor.current === userId) return;
    loadedFor.current = userId;
    warned.current = false;
    if (Object.keys(pendingValues(userId)).length) enqueue(userId, {});
  }, [userId, enqueue]);

  const flush = useCallback(() => {
    timer.current = null;
    const id = userRef.current;
    const values = queued.current;
    queued.current = {};
    if (id != null) enqueue(id, values);
  }, [enqueue]);

  // Otimista: aplica na hora e agenda um PATCH (debounce) que junta as chaves alteradas.
  const setPref = useCallback<SetPref>(
    (key, value) => {
      setPrefs((current) => ({ ...current, [key]: value }));
      queued.current = { ...queued.current, [key]: value };
      if (userRef.current != null) savePending(userRef.current, { [key]: value });
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, SAVE_DEBOUNCE_MS);
    },
    [flush]
  );

  // Logout/recarga/fechar aba navegam a página inteira (sem cleanup do React): tenta enviar já ao esconder.
  // Se o envio não completar, a pendência já está no storage e vai no próximo load.
  useEffect(() => {
    const flushNow = () => {
      if (!timer.current) return;
      clearTimeout(timer.current);
      flush();
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flushNow();
    };
    window.addEventListener("pagehide", flushNow);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flushNow);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [flush]);

  // Saiu da árvore com gravação agendada: guarda como pendência (vai no próximo load).
  useEffect(
    () => () => {
      if (!timer.current) return;
      clearTimeout(timer.current);
      const id = userRef.current;
      if (id != null && Object.keys(queued.current).length) {
        savePending(id, queued.current);
      }
    },
    []
  );

  const value = useMemo(() => ({ prefs, setPref }), [prefs, setPref]);
  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  return useContext(PreferencesContext);
}
